import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import logoPlatform from '../assets/narekomu-ai-platform.webp';
import { Global, css } from '@emotion/react';

/* ─── Types ─── */
interface LoginPageProps {
  onLogin: (username: string, password: string) => boolean;
}

/* ─── @keyframes ─── */
const KEYFRAMES = `
@keyframes lp-gradient-shift {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes lp-float {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-12px); }
}
@keyframes lp-fade-in-up {
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes lp-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes lp-pulse-ring {
  0%   { transform: scale(0.8); opacity: 0.6; }
  50%  { transform: scale(1.2); opacity: 0; }
  100% { transform: scale(0.8); opacity: 0; }
}
@keyframes lp-count-flash {
  0%   { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes lp-grid-glow {
  0%, 100% { opacity: 0.03; }
  50%      { opacity: 0.07; }
}
@keyframes lp-orb-drift-1 {
  0%   { transform: translate(0, 0) scale(1); }
  33%  { transform: translate(60px, -40px) scale(1.1); }
  66%  { transform: translate(-30px, 30px) scale(0.95); }
  100% { transform: translate(0, 0) scale(1); }
}
@keyframes lp-orb-drift-2 {
  0%   { transform: translate(0, 0) scale(1); }
  33%  { transform: translate(-50px, 50px) scale(1.05); }
  66%  { transform: translate(40px, -20px) scale(0.9); }
  100% { transform: translate(0, 0) scale(1); }
}
@keyframes lp-shimmer {
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
}
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
`;

/* ─── Design Tokens ─── */
const T = {
  bg: {
    darkest: '#070e1a',
    dark: '#0a1628',
    mid: '#102A43',
    card: 'rgba(16, 42, 67, 0.55)',
    glass: 'rgba(16, 42, 67, 0.35)',
  },
  primary: '#2680C2',
  primaryLight: '#62B0E8',
  primaryPale: '#B6E0FE',
  primaryDark: '#0A558C',
  accent: '#38BEC9',
  success: '#57AE5B',
  warning: '#F0B429',
  critical: '#E12D39',
  text: {
    heading: '#F0F4F8',
    body: '#BCCCDC',
    muted: '#829AB1',
    inverse: '#102A43',
  },
  font: "'Noto Sans JP', 'Inter', 'Hiragino Sans', 'Yu Gothic UI', system-ui, -apple-system, sans-serif",
  radius: { sm: '8px', md: '12px', lg: '16px', xl: '24px' },
  shadow: {
    glass: '0 8px 32px rgba(0,0,0,0.3)',
    card: '0 4px 24px rgba(0,0,0,0.2)',
    glow: '0 0 60px rgba(38,128,194,0.15)',
  },
} as const;

/* ─── Helpers ─── */

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function AnimatedNumber({ value, suffix = '', duration = 1600 }: { value: number; suffix?: string; duration?: number }) {
  const [display, setDisplay] = useState(0);
  const iv = useInView(0.3);
  useEffect(() => {
    if (!iv.visible) return;
    let start: number | null = null;
    let raf: number;
    const step = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * value));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [iv.visible, value, duration]);
  return (
    <span ref={iv.ref} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {display.toLocaleString()}{suffix}
    </span>
  );
}

/* ─── Feature data ─── */
const FEATURES = [
  {
    icon: <SmartToyOutlinedIcon sx={{ fontSize: 32 }} />,
    title: 'インシデント自動検知・初動対応',
    description: '異常を検知し、AIが運用手順書に沿って初動対応を自動実行。深夜3時のアラートでも、チームを起こさず対処を完了します。平均復旧時間を72%短縮。',
    color: T.primary,
  },
  {
    icon: <SavingsOutlinedIcon sx={{ fontSize: 32 }} />,
    title: 'クラウド & データ基盤のコスト最適化',
    description: 'AWS・Azure・Databricksの利用状況を横断分析。GPU停止忘れやアイドルクラスターも自動検知し、最適プランを提案します。年間平均30%のコスト削減を達成。',
    color: T.success,
  },
  {
    icon: <ShieldOutlinedIcon sx={{ fontSize: 32 }} />,
    title: 'セキュリティ監視と規定準拠',
    description: 'アクセス権限、脆弱性、規定への準拠状況をリアルタイムで監視。問題を検知した時点で担当者へ通知し、対処手順を自動で提示します。',
    color: T.warning,
  },
  {
    icon: <ChatOutlinedIcon sx={{ fontSize: 32 }} />,
    title: '自然言語でクラウド & データ運用',
    description: '日本語で話しかけるだけで、クラウドの運用状況からDatabricksジョブの実行結果まで確認。「先月のAWSコストを部署別に見せて」——そんな指示で十分です。',
    color: T.accent,
  },
];

const STATS = [
  { value: 72, suffix: '%', label: '障害からの復旧時間を短縮' },
  { value: 30, suffix: '%', label: '年間クラウドコストを削減' },
  { value: 150, suffix: '+', label: '対応するクラウドサービス' },
  { value: 10000, suffix: '+', label: '毎月の自動処理件数' },
];

/* ─── Sub-components ─── */

/** Glassmorphism login form — primary action, always visible */
function LoginForm({
  onLogin,
}: {
  onLogin: (u: string, p: string) => boolean;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!onLogin(username, password)) setError(true);
  };

  // Global CSS to override browser autofill styles.
  // This is injected as a <style> tag in <head>, bypassing all specificity issues.
  // LoginPage renders OUTSIDE the MUI ThemeProvider, so theme-level overrides don't apply here.
  const autofillGlobalStyles = css`
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active,
    input:autofill,
    input:autofill:hover,
    input:autofill:focus,
    input:autofill:active {
      -webkit-box-shadow: 0 0 0 1000px #0B1929 inset !important;
      box-shadow: 0 0 0 1000px #0B1929 inset !important;
      -webkit-text-fill-color: #FFFFFF !important;
      caret-color: #FFFFFF !important;
      border-radius: inherit;
      transition: background-color 5000s ease-in-out 0s;
    }
  `;

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      color: '#FFFFFF',
      backgroundColor: 'rgba(255,255,255,0.14)',
      borderRadius: T.radius.sm,
      fontSize: '0.95rem',
      '& fieldset': { borderColor: 'rgba(188,204,220,0.50)', borderWidth: '1.5px' },
      '&:hover fieldset': { borderColor: 'rgba(98,176,232,0.65)' },
      '&.Mui-focused fieldset': { borderColor: T.primaryLight, borderWidth: '2px' },
      '& input': { padding: '14px 16px', color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF' },
      '& input::placeholder': { color: T.text.muted, opacity: 1 },
    },
    '& .MuiInputLabel-root': { color: '#E0E8F0', fontSize: '0.9rem', fontWeight: 500 },
    '& .MuiInputLabel-root.Mui-focused': { color: T.primaryLight, fontWeight: 600 },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': { color: '#E0E8F0' },
  };

  return (
    <>
    <Global styles={autofillGlobalStyles} />
    <Box
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: T.radius.lg,
        bgcolor: 'rgba(10, 21, 40, 0.92)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        border: '1px solid rgba(98,176,232,0.22)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
        width: '100%',
        maxWidth: 380,
        animation: 'lp-fade-in-up 0.8s ease-out 0.3s both',
      }}
    >
      {/* Form header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: T.radius.sm,
            background: `linear-gradient(135deg, ${T.primary}30, ${T.accent}20)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LockOutlinedIcon sx={{ color: T.primaryLight, fontSize: 18 }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontFamily: T.font,
              fontWeight: 700,
              fontSize: '1rem',
              color: T.text.heading,
              letterSpacing: '0.02em',
              lineHeight: 1.2,
            }}
          >
            OPS Hub にログイン
          </Typography>
          <Typography
            sx={{
              fontFamily: T.font,
              fontSize: '0.75rem',
              color: T.text.muted,
              mt: 0.25,
            }}
          >
            運用ダッシュボードにアクセス
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: T.radius.sm,
            bgcolor: 'rgba(225,45,57,0.12)',
            color: '#F86A6A',
            fontSize: '0.85rem',
            '& .MuiAlert-icon': { color: '#F86A6A' },
          }}
        >
          ユーザー名またはパスワードに誤りがあります。再度ご入力ください。
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          label="ユーザー名"
          fullWidth
          size="small"
          value={username}
          onChange={(e) => { setError(false); setUsername(e.target.value); }}
          sx={{ mb: 2, ...inputSx }}
          autoComplete="username"
          autoFocus
        />
        <TextField
          label="パスワード"
          type={showPw ? 'text' : 'password'}
          fullWidth
          size="small"
          value={password}
          onChange={(e) => { setError(false); setPassword(e.target.value); }}
          sx={{ mb: 3, ...inputSx }}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPw(!showPw)}
                  edge="end"
                  size="small"
                  sx={{ color: T.text.muted }}
                  aria-label={showPw ? 'パスワードを隠す' : 'パスワードを表示'}
                >
                  {showPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          sx={{
            py: 1.5,
            fontFamily: T.font,
            fontWeight: 700,
            fontSize: '0.95rem',
            letterSpacing: '0.04em',
            bgcolor: T.primary,
            borderRadius: T.radius.sm,
            textTransform: 'none',
            boxShadow: '0 4px 20px rgba(38,128,194,0.4)',
            transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
            '&:hover': {
              bgcolor: '#1A74B8',
              boxShadow: '0 6px 28px rgba(38,128,194,0.55)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          ログインして運用状況を確認
        </Button>
      </form>
    </Box>
    </>
  );
}

/** Feature card */
function FeatureCard({
  icon,
  title,
  description,
  color,
  index,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  index: number;
}) {
  const iv = useInView(0.15);
  return (
    <Box
      ref={iv.ref}
      sx={{
        p: 3,
        borderRadius: T.radius.md,
        background: T.bg.card,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(188,204,220,0.08)',
        transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
        opacity: iv.visible ? 1 : 0,
        transform: iv.visible ? 'translateY(0)' : 'translateY(32px)',
        transitionDelay: `${index * 120}ms`,
        cursor: 'default',
        height: '100%',
        '&:hover': {
          background: 'rgba(16, 42, 67, 0.7)',
          border: `1px solid ${color}33`,
          boxShadow: `0 8px 32px ${color}18`,
          transform: 'translateY(-4px)',
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: T.radius.md,
          background: `${color}14`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color,
          mb: 2,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontFamily: T.font,
          fontWeight: 700,
          fontSize: '1rem',
          color: T.text.heading,
          lineHeight: 1.4,
          mb: 1,
          letterSpacing: '0.02em',
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: T.font,
          fontWeight: 400,
          fontSize: '0.85rem',
          color: T.text.body,
          lineHeight: 1.75,
          letterSpacing: '0.03em',
        }}
      >
        {description}
      </Typography>
    </Box>
  );
}

/* ─── Main Component ─── */
export function LoginPage({ onLogin }: LoginPageProps) {
  const [keyframesInjected, setKeyframesInjected] = useState(false);
  const featuresRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (keyframesInjected) return;
    const style = document.createElement('style');
    style.textContent = KEYFRAMES;
    document.head.appendChild(style);
    setKeyframesInjected(true);
    return () => { document.head.removeChild(style); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statsIv = useInView(0.2);
  const ctaIv = useInView(0.15);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(170deg, ${T.bg.darkest} 0%, ${T.bg.dark} 30%, ${T.bg.mid} 100%)`,
        fontFamily: T.font,
        overflowX: 'hidden',
        scrollBehavior: 'smooth',
      }}
    >
      {/* ═══════════════════════════════════════════════════
          SECTION 1 — Hero + Login (First Viewport)
          ログインフォームはファーストビューの中心。
          モバイルでもスクロール不要でログイン可能。
          ═══════════════════════════════════════════════════ */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Background: animated gradient overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `
              radial-gradient(ellipse 80% 60% at 20% 50%, rgba(38,128,194,0.12) 0%, transparent 70%),
              radial-gradient(ellipse 60% 80% at 80% 30%, rgba(56,190,201,0.08) 0%, transparent 60%)
            `,
            animation: 'lp-gradient-shift 12s ease infinite',
            backgroundSize: '200% 200%',
          }}
        />
        {/* Background: grid pattern */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(38,128,194,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(38,128,194,0.04) 1px, transparent 1px)
            `,
            backgroundSize: '64px 64px',
            animation: 'lp-grid-glow 8s ease-in-out infinite',
          }}
        />
        {/* Background: floating orbs */}
        <Box
          sx={{
            position: 'absolute',
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(38,128,194,0.12) 0%, transparent 70%)',
            top: '10%',
            left: '-5%',
            filter: 'blur(40px)',
            animation: 'lp-orb-drift-1 20s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(56,190,201,0.1) 0%, transparent 70%)',
            bottom: '15%',
            right: '5%',
            filter: 'blur(40px)',
            animation: 'lp-orb-drift-2 16s ease-in-out infinite',
          }}
        />

        {/* Hero Content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 4, md: 0 } }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" direction={{ xs: 'column-reverse', md: 'row' }}>
            {/* Left: Copy */}
            <Grid item xs={12} md={7}>
              <Box sx={{ animation: 'lp-fade-in-up 0.9s ease-out both' }}>
                {/* Logo */}
                <Box sx={{ mb: { xs: 2, md: 4 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <img
                    src={logoPlatform}
                    alt="ナレコム AI Platform"
                    style={{ height: 36, objectFit: 'contain' }}
                  />
                </Box>

                {/* Headline */}
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: T.font,
                    fontWeight: 900,
                    fontSize: { xs: '2rem', sm: '2.8rem', md: '3.4rem' },
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                    color: T.text.heading,
                    mb: 2.5,
                  }}
                >
                  {'Data & AI 運用を、'}
                  <br />
                  <Box
                    component="span"
                    sx={{
                      color: 'transparent',
                      backgroundImage: `linear-gradient(135deg, ${T.primary}, ${T.accent})`,
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                    }}
                  >
                    {'エージェント'}
                  </Box>
                  {'が変える'}
                </Typography>

                {/* Sub-headline */}
                <Typography
                  sx={{
                    fontFamily: T.font,
                    fontWeight: 400,
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    lineHeight: 1.8,
                    letterSpacing: '0.03em',
                    color: T.text.body,
                    maxWidth: 540,
                    mb: { xs: 2, md: 4 },
                  }}
                >
                  AWS・Azure からDatabricksまで——インシデント対応、コスト最適化、データ品質監視を
                  <Box component="br" sx={{ display: { xs: 'none', md: 'block' } }} />
                  AIエージェントが24時間カバー。クラウドもデータ基盤も、まとめて任せられます。
                </Typography>

                {/* Trust badge (desktop only) */}
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    gap: 3,
                    animation: 'lp-fade-in 1.2s ease-out 0.6s both',
                  }}
                >
                  {[
                    { num: '72%', label: '復旧時間短縮' },
                    { num: '30%', label: 'コスト削減' },
                    { num: '150+', label: '対応サービス' },
                  ].map((stat) => (
                    <Box key={stat.label} sx={{ textAlign: 'center' }}>
                      <Typography
                        sx={{
                          fontFamily: T.font,
                          fontWeight: 800,
                          fontSize: '1.5rem',
                          color: T.text.heading,
                          lineHeight: 1.1,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {stat.num}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: T.font,
                          fontSize: '0.7rem',
                          color: T.text.muted,
                          mt: 0.25,
                          letterSpacing: '0.04em',
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Right: Login Form — PRIMARY ACTION */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', md: 'flex-end' },
              }}
            >
              <LoginForm onLogin={onLogin} />
            </Grid>
          </Grid>
        </Container>

        {/* Scroll indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 12, md: 32 },
            left: '50%',
            transform: 'translateX(-50%)',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            animation: 'lp-float 3s ease-in-out infinite',
            opacity: 0.6,
          }}
          onClick={() => featuresRef.current?.scrollIntoView({ behavior: 'smooth' })}
        >
          <Typography
            sx={{
              fontFamily: T.font,
              fontSize: '0.65rem',
              color: T.text.muted,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}
          >
            詳しく見る
          </Typography>
          <KeyboardArrowDownIcon sx={{ color: T.text.muted, fontSize: 18 }} />
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════
          SECTION 2 — Features
          ═══════════════════════════════════════════════════ */}
      <Box ref={featuresRef} sx={{ py: { xs: 8, md: 12 }, position: 'relative' }}>
        {/* Section divider glow */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 240,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${T.primary}44, transparent)`,
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography
              sx={{
                fontFamily: T.font,
                fontWeight: 600,
                fontSize: '0.8rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: T.primary,
                mb: 1.5,
              }}
            >
              Features
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: T.font,
                fontWeight: 800,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
                color: T.text.heading,
                mb: 2,
              }}
            >
              運用の負担を減らす、4つの力
            </Typography>
            <Typography
              sx={{
                fontFamily: T.font,
                fontSize: '1rem',
                color: T.text.muted,
                lineHeight: 1.75,
                letterSpacing: '0.03em',
                maxWidth: 520,
                mx: 'auto',
              }}
            >
              クラウドインフラからデータ基盤まで、AIエージェントが検知・判断・対処を一気通貫で担います
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {FEATURES.map((f, i) => (
              <Grid item xs={12} sm={6} md={3} key={f.title}>
                <FeatureCard {...f} index={i} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          SECTION 2.5 — Why Us / Strengths
          Data & AI のための運用支援という独自の立ち位置
          ═══════════════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 8, md: 12 }, position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 240,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${T.accent}44, transparent)`,
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography
              sx={{
                fontFamily: T.font,
                fontWeight: 600,
                fontSize: '0.8rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: T.accent,
                mb: 1.5,
              }}
            >
              Why OPS Hub
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: T.font,
                fontWeight: 800,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
                color: T.text.heading,
                mb: 2,
              }}
            >
              Data & AI のための運用支援
            </Typography>
            <Typography
              sx={{
                fontFamily: T.font,
                fontSize: '1rem',
                color: T.text.muted,
                lineHeight: 1.75,
                letterSpacing: '0.03em',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              クラウドインフラだけではありません。Databricks や AI ワークロードまで含めた
              統合運用支援ができるのは、私たちの専門性があるからです。
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              {
                icon: <CloudOutlinedIcon sx={{ fontSize: 28 }} />,
                title: 'AWS & Azure',
                subtitle: 'マルチクラウド運用',
                description: 'EC2・Lambda・AKS・App Service——主要サービスの監視・コスト管理・障害対応を横断的にカバー。マルチクラウド環境を一元管理します。',
                color: T.primary,
              },
              {
                icon: <StorageOutlinedIcon sx={{ fontSize: 28 }} />,
                title: 'Databricks',
                subtitle: 'データ基盤の運用自動化',
                description: 'ジョブの異常検知、アイドルクラスター停止、OOMエラー自動復旧、データ品質ゲートまで。データエンジニアの運用負荷を大幅に削減します。',
                color: '#FF6F00',
              },
              {
                icon: <InsightsOutlinedIcon sx={{ fontSize: 28 }} />,
                title: 'AI ワークロード',
                subtitle: 'GPU・モデル運用の最適化',
                description: 'GPU インスタンスの停止忘れ検知、推論エンドポイントの監視、学習ジョブのコスト可視化。AI 開発チームの「うっかりコスト」を防ぎます。',
                color: T.accent,
              },
              {
                icon: <SecurityOutlinedIcon sx={{ fontSize: 28 }} />,
                title: 'AI ガバナンス',
                subtitle: 'LLM の品質とデータを監視',
                description: 'AI Foundry・Bedrock 上の LLM データ利用監視、LLM Judge による出力精度の自動評価、MLflow でのモデル品質トラッキング。AI 運用に必要なガバナンス基盤を提供します。',
                color: T.warning,
              },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={3} key={item.title}>
                <Box
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: T.radius.md,
                    background: 'rgba(16, 42, 67, 0.4)',
                    border: '1px solid rgba(188,204,220,0.06)',
                    transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
                    '&:hover': {
                      background: 'rgba(16, 42, 67, 0.65)',
                      border: `1px solid ${item.color}33`,
                      boxShadow: `0 8px 32px ${item.color}12`,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: T.radius.sm,
                      background: `${item.color}18`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: item.color,
                      mb: 2,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: T.font,
                      fontWeight: 800,
                      fontSize: '1.05rem',
                      color: T.text.heading,
                      lineHeight: 1.3,
                      mb: 0.5,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: T.font,
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: item.color,
                      letterSpacing: '0.03em',
                      mb: 1.5,
                    }}
                  >
                    {item.subtitle}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: T.font,
                      fontWeight: 400,
                      fontSize: '0.85rem',
                      color: T.text.body,
                      lineHeight: 1.75,
                      letterSpacing: '0.03em',
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          SECTION 2.6 — Databricks Operations
          Databricks 運用・保守の具体例
          ═══════════════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 8, md: 12 }, position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 240,
            height: 1,
            background: `linear-gradient(90deg, transparent, #FF6F0066, transparent)`,
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography
              sx={{
                fontFamily: T.font,
                fontWeight: 600,
                fontSize: '0.8rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: '#FF6F00',
                mb: 1.5,
              }}
            >
              Databricks Operations
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: T.font,
                fontWeight: 800,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
                color: T.text.heading,
                mb: 2,
              }}
            >
              Databricks 運用・保守の具体例
            </Typography>
            <Typography
              sx={{
                fontFamily: T.font,
                fontSize: '1rem',
                color: T.text.muted,
                lineHeight: 1.75,
                letterSpacing: '0.03em',
                maxWidth: 640,
                mx: 'auto',
              }}
            >
              Lakehouse の安定稼働に必要なジョブ監視、コスト管理、障害対応を
              ランブック連携と自動化で実現します。
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              {
                icon: <NotificationsActiveOutlinedIcon sx={{ fontSize: 24 }} />,
                title: 'LLMOps ジョブ監視',
                platform: 'Workflows / Lakeflow',
                description: 'Databricks Workflows のジョブ実行をリアルタイム監視。失敗・遅延・長時間実行を検知し、Slack・Webhook で即時通知。MLflow GenAI トレーシングで LLM パイプラインの挙動も可視化します。',
                color: '#FF6F00',
              },
              {
                icon: <DescriptionOutlinedIcon sx={{ fontSize: 24 }} />,
                title: 'ランブック自動実行',
                platform: 'OPS Hub 独自機能',
                description: '障害パターンごとにランブック（対応手順書）を事前登録。ジョブ失敗やクラスター異常を検知すると、登録済みの復旧手順を自動実行。OOM エラーのクラスタースケールアップやジョブ再実行まで無人対応します。',
                color: T.accent,
              },
              {
                icon: <AttachMoneyOutlinedIcon sx={{ fontSize: 24 }} />,
                title: 'FinOps / DBU コスト管理',
                platform: 'System Tables / Unity Catalog',
                description: 'system.billing.usage テーブルから DBU 消費をワークスペース・チーム・ジョブ単位で自動集計。アイドルクラスターの検知、サーバーレスへの移行提案、月次コストレポートで無駄を可視化します。',
                color: T.success,
              },
              {
                icon: <TableChartOutlinedIcon sx={{ fontSize: 24 }} />,
                title: 'Lakehouse データ品質監視',
                platform: 'Lakehouse Monitoring',
                description: 'Delta テーブルの統計的プロファイリングと異常検知を自動実行。スキーマドリフト、NULL 率の急増、データ鮮度の劣化をアラート。データ品質ゲートで後続パイプラインへの汚染を防ぎます。',
                color: T.primary,
              },
              {
                icon: <RocketLaunchOutlinedIcon sx={{ fontSize: 24 }} />,
                title: 'MLflow モデルライフサイクル',
                platform: 'MLflow / Unity Catalog',
                description: 'モデルの学習実験追跡からレジストリ登録、ステージング→本番昇格、精度ドリフト検知まで一気通貫で管理。問題発生時はワンクリックで前バージョンにロールバック可能です。',
                color: T.primaryLight,
              },
              {
                icon: <TuneOutlinedIcon sx={{ fontSize: 24 }} />,
                title: 'クラスター最適化',
                platform: 'Predictive Optimization',
                description: 'Predictive Optimization による自動チューニング、クラスターポリシーの一括管理、オートターミネーション設定の監査。ジョブ計算とサーバーレスの最適な使い分けを提案します。',
                color: T.warning,
              },
            ].map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.title}>
                <Box
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: T.radius.md,
                    background: 'rgba(16, 42, 67, 0.35)',
                    border: '1px solid rgba(188,204,220,0.06)',
                    transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
                    '&:hover': {
                      background: 'rgba(16, 42, 67, 0.6)',
                      border: `1px solid ${item.color}33`,
                      boxShadow: `0 8px 32px ${item.color}10`,
                      transform: 'translateY(-3px)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: T.radius.sm,
                        background: `${item.color}14`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: item.color,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: T.font,
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          color: T.text.heading,
                          lineHeight: 1.3,
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: T.font,
                          fontWeight: 500,
                          fontSize: '0.65rem',
                          color: item.color,
                          letterSpacing: '0.02em',
                          opacity: 0.85,
                        }}
                      >
                        {item.platform}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: T.font,
                      fontWeight: 400,
                      fontSize: '0.83rem',
                      color: T.text.body,
                      lineHeight: 1.75,
                      letterSpacing: '0.03em',
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          SECTION 2.7 — Concrete Use Cases
          AI ガバナンス・監視の具体例
          ═══════════════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 8, md: 12 }, position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 240,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${T.warning}44, transparent)`,
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography
              sx={{
                fontFamily: T.font,
                fontWeight: 600,
                fontSize: '0.8rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: T.warning,
                mb: 1.5,
              }}
            >
              Use Cases
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: T.font,
                fontWeight: 800,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
                color: T.text.heading,
                mb: 2,
              }}
            >
              AI Foundry・Bedrock 運用の具体例
            </Typography>
            <Typography
              sx={{
                fontFamily: T.font,
                fontSize: '1rem',
                color: T.text.muted,
                lineHeight: 1.75,
                letterSpacing: '0.03em',
                maxWidth: 640,
                mx: 'auto',
              }}
            >
              LLM を本番運用する企業が直面する「品質・コスト・ガバナンス」の課題を、
              具体的な監視と自動化で解決します。
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              {
                icon: <DatasetOutlinedIcon sx={{ fontSize: 24 }} />,
                title: 'LLM データ利用監視',
                platform: 'AI Foundry / Bedrock',
                description: 'LLM が参照・生成するデータの流れをリアルタイムで追跡。機密情報の意図しない漏洩や、学習データの不正利用を即座に検知・ブロックします。',
                color: T.primary,
              },
              {
                icon: <GavelOutlinedIcon sx={{ fontSize: 24 }} />,
                title: 'LLM Judge 精度監視',
                platform: 'AI Foundry / Bedrock',
                description: 'LLM の出力品質を LLM Judge で自動評価。ハルシネーション率、回答の一貫性、ポリシー準拠をスコアリングし、品質低下を即時アラート。',
                color: T.critical,
              },
              {
                icon: <AutoGraphOutlinedIcon sx={{ fontSize: 24 }} />,
                title: 'MLflow モニタリング可視化',
                platform: 'Databricks / MLflow',
                description: 'モデルの学習実験、デプロイバージョン、推論レイテンシ、精度ドリフトを一元可視化。いつ・どのモデルが劣化したかを即座に特定できます。',
                color: '#FF6F00',
              },
              {
                icon: <MemoryOutlinedIcon sx={{ fontSize: 24 }} />,
                title: 'GPU / 推論コスト最適化',
                platform: 'AI Foundry / Bedrock',
                description: 'トークン使用量の部署別トラッキング、アイドル GPU の自動停止、プロビジョンドスループットの適正化。月次レポートで無駄を可視化します。',
                color: T.accent,
              },
              {
                icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 24 }} />,
                title: 'AI コンプライアンス監査',
                platform: 'AI Foundry / Bedrock',
                description: 'Content Safety フィルタの動作ログ、Responsible AI ポリシーの準拠状況、モデル利用のアクセス制御。監査対応に必要なエビデンスを自動収集します。',
                color: T.success,
              },
              {
                icon: <MonitorHeartOutlinedIcon sx={{ fontSize: 24 }} />,
                title: 'エンドポイントヘルス監視',
                platform: 'AI Foundry / Bedrock / SageMaker',
                description: 'リアルタイム推論・バッチ推論エンドポイントの可用性、レイテンシ、エラー率を継続監視。異常検知時は自動でフォールバックを発動します。',
                color: T.primaryLight,
              },
            ].map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.title}>
                <Box
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: T.radius.md,
                    background: 'rgba(16, 42, 67, 0.35)',
                    border: '1px solid rgba(188,204,220,0.06)',
                    transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
                    '&:hover': {
                      background: 'rgba(16, 42, 67, 0.6)',
                      border: `1px solid ${item.color}33`,
                      boxShadow: `0 8px 32px ${item.color}10`,
                      transform: 'translateY(-3px)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: T.radius.sm,
                        background: `${item.color}14`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: item.color,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: T.font,
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          color: T.text.heading,
                          lineHeight: 1.3,
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: T.font,
                          fontWeight: 500,
                          fontSize: '0.65rem',
                          color: item.color,
                          letterSpacing: '0.02em',
                          opacity: 0.85,
                        }}
                      >
                        {item.platform}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: T.font,
                      fontWeight: 400,
                      fontSize: '0.83rem',
                      color: T.text.body,
                      lineHeight: 1.75,
                      letterSpacing: '0.03em',
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          SECTION 3 — Stats / Social Proof
          ═══════════════════════════════════════════════════ */}
      <Box
        ref={statsIv.ref}
        sx={{
          py: { xs: 7, md: 10 },
          position: 'relative',
          background: `linear-gradient(180deg, transparent 0%, rgba(38,128,194,0.04) 50%, transparent 100%)`,
        }}
      >
        <Container maxWidth="md">
          <Grid container spacing={4} justifyContent="center">
            {STATS.map((s, i) => (
              <Grid item xs={6} sm={3} key={s.label}>
                <Box
                  sx={{
                    textAlign: 'center',
                    opacity: statsIv.visible ? 1 : 0,
                    animation: statsIv.visible
                      ? `lp-count-flash 0.6s ease-out ${i * 150}ms both`
                      : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: T.font,
                      fontWeight: 800,
                      fontSize: { xs: '2rem', md: '2.75rem' },
                      lineHeight: 1.1,
                      color: T.text.heading,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <AnimatedNumber
                      value={s.value}
                      suffix={s.suffix}
                      duration={1400 + i * 200}
                    />
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: T.font,
                      fontWeight: 500,
                      fontSize: '0.85rem',
                      color: T.text.muted,
                      mt: 0.75,
                      letterSpacing: '0.04em',
                    }}
                  >
                    {s.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          SECTION 4 — Contact / CTA
          ═══════════════════════════════════════════════════ */}
      <Box
        ref={ctaIv.ref}
        sx={{
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background glow */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(38,128,194,0.08) 0%, transparent 70%)',
            filter: 'blur(60px)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              textAlign: 'center',
              opacity: ctaIv.visible ? 1 : 0,
              transform: ctaIv.visible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'all 0.7s ease-out',
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontFamily: T.font,
                fontWeight: 800,
                fontSize: { xs: '1.5rem', md: '2rem' },
                lineHeight: 1.35,
                letterSpacing: '-0.01em',
                color: T.text.heading,
                mb: 1.5,
              }}
            >
              まずは、運用の現状を相談しませんか
            </Typography>
            <Typography
              sx={{
                fontFamily: T.font,
                fontSize: '0.95rem',
                color: T.text.muted,
                lineHeight: 1.75,
                letterSpacing: '0.03em',
                mb: 5,
              }}
            >
              導入前のご相談から、デモ体験、料金プランの確認まで対応します。
              <br />
              まずはお気軽にご連絡ください。
            </Typography>

            {/* Primary CTA button */}
            <Button
              variant="contained"
              size="large"
              href="mailto:marketing@knowledgecommunication.jp?subject=%E3%83%8A%E3%83%AC%E3%82%B3%E3%83%A0+AI+Platform+OPS+Hub+%E3%81%8A%E5%95%8F%E3%81%84%E5%90%88%E3%82%8F%E3%81%9B"
              sx={{
                py: 1.5,
                px: 5,
                fontFamily: T.font,
                fontWeight: 700,
                fontSize: '0.95rem',
                letterSpacing: '0.04em',
                bgcolor: T.primary,
                color: '#FFFFFF',
                borderRadius: T.radius.sm,
                textTransform: 'none',
                boxShadow: '0 4px 20px rgba(38,128,194,0.4)',
                transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                mb: 4,
                '&:hover': {
                  bgcolor: '#1A74B8',
                  boxShadow: '0 6px 28px rgba(38,128,194,0.55)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              無料で相談する
            </Button>

            {/* Contact info — inline with divider */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 2, sm: 3 },
                flexWrap: 'wrap',
              }}
            >
              <Box
                component="a"
                href="mailto:marketing@knowledgecommunication.jp"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  color: T.text.body,
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: T.primaryLight },
                }}
              >
                <EmailOutlinedIcon sx={{ fontSize: 18, color: T.primaryLight }} />
                <Typography
                  sx={{
                    fontFamily: T.font,
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                  }}
                >
                  marketing@knowledgecommunication.jp
                </Typography>
              </Box>

              <Box
                sx={{
                  width: '1px',
                  height: 16,
                  bgcolor: 'rgba(188,204,220,0.2)',
                  display: { xs: 'none', sm: 'block' },
                }}
              />

              <Box
                component="a"
                href="tel:047-397-8897"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  color: T.text.body,
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: T.primaryLight },
                }}
              >
                <PhoneOutlinedIcon sx={{ fontSize: 18, color: T.primaryLight }} />
                <Typography
                  sx={{
                    fontFamily: T.font,
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                  }}
                >
                  047-397-8897
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          Footer
          ═══════════════════════════════════════════════════ */}
      <Box
        sx={{
          py: 3,
          textAlign: 'center',
          borderTop: '1px solid rgba(188,204,220,0.06)',
        }}
      >
        <Typography
          sx={{
            fontFamily: T.font,
            fontSize: '0.75rem',
            color: T.text.muted,
            letterSpacing: '0.02em',
          }}
        >
          &copy; {new Date().getFullYear()} KNOWLEDGECOMMUNICATION Co.,Ltd. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}
